import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useDraggable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesPostState } from "../../../Enums/voices";
import { VoicesCalendarPost } from "../../../Models/voicesCalendar";

interface Props {
  post: VoicesCalendarPost;
  compact: boolean;
  onOpen: (postId: number) => void;
}

export const STATE_COLORS: Record<VoicesPostState, string> = {
  [VoicesPostState.DRAFT]: color.gray500,
  [VoicesPostState.IN_APPROVAL]: color.orange200,
  [VoicesPostState.SCHEDULED]: color.orange600,
  [VoicesPostState.PUBLISHED]: color.green700,
  [VoicesPostState.FAILED]: color.red700,
};

export const STATE_KEYS: Record<VoicesPostState, string> = {
  [VoicesPostState.DRAFT]: "stateDraft",
  [VoicesPostState.IN_APPROVAL]: "stateInApproval",
  [VoicesPostState.SCHEDULED]: "stateScheduled",
  [VoicesPostState.PUBLISHED]: "statePublished",
  [VoicesPostState.FAILED]: "stateFailed",
};

const VoicesCalendarPostChip: React.FC<Props> = ({ post, compact, onOpen }) => {
  const { t } = useTranslation("voicesPosts");
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: post.id,
    disabled: !post.can_move,
  });
  const time = post.scheduled_at.slice(11, 16);
  const stripe = post.rejected ? color.red700 : STATE_COLORS[post.state];

  const details = (
    <Box sx={{ maxWidth: 280 }}>
      <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "white" }}>
        {time} · {post.target.name}
      </Typography>
      {post.campaign_name && (
        <Typography variant="caption" sx={{ display: "block", color: "white" }}>
          {post.campaign_name}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: "block", color: "white", opacity: 0.85 }}>
        {post.rejected ? t("stateRejected") : t(STATE_KEYS[post.state])}
        {!post.can_move && ` · ${t("calendar_notMovable")}`}
      </Typography>
      {post.preview && (
        <Typography variant="caption" sx={{ display: "block", color: "white", mt: 0.5 }}>
          {post.preview}
          {post.preview.length >= 160 ? "…" : ""}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={details} placement="right" enterDelay={300} disableInteractive>
      <Box
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={() => onOpen(post.id)}
        sx={{
          px: 0.75,
          py: compact ? 0.25 : 0.5,
          mb: 0.5,
          borderRadius: 1,
          backgroundColor: "white",
          border: `1px solid ${color.gray200}`,
          borderLeft: `3px solid ${stripe}`,
          cursor: post.can_move ? "grab" : "pointer",
          opacity: isDragging ? 0.6 : 1,
          transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
          zIndex: isDragging ? 10 : "auto",
          position: "relative",
          "&:hover": { backgroundColor: color.orange50 },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 600,
            color: color.gray900,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: compact ? "0.72rem" : undefined,
          }}
        >
          {time} · {post.target.name}
        </Typography>
        {!compact && (
          <Typography
            variant="caption"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.3,
            }}
          >
            {post.preview || "…"}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default VoicesCalendarPostChip;
